import React, { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PostDataType, PostRepository } from '@amityco/js-sdk';
import { FormattedMessage, useIntl } from 'react-intl';
import usePost from '../../../../social/hooks/usePost';
import useSocialMention from '../../../../social/hooks/useSocialMention';
import Content from './Content';
import { PostEditorContainer, Footer, ContentContainer, PostButton } from './styles';
import { extractMetadata, parseMentionsMarkup } from '../../../../helpers/utils';
const PostEditor = ({ postId, onSave, className }) => {
    var _a;
    const { formatMessage } = useIntl();
    const { post, handleUpdatePost, childrenPosts = [] } = usePost(postId);
    const { data, dataType, targetId, targetType, metadata, mentionees: postMentionees } = post;
    const { text, markup, mentions, clearAll, onChange, queryMentionees } = useSocialMention({
        targetId,
        targetType,
        remoteText: (_a = data === null || data === void 0 ? void 0 : data.text) !== null && _a !== void 0 ? _a : '',
        remoteMarkup: parseMentionsMarkup(data === null || data === void 0 ? void 0 : data.text, metadata),
    });
    // Children posts of the post being rendered with postId.
    const [localChildrenPosts, setLocalChildrenPosts] = useState(childrenPosts);
    useEffect(() => setLocalChildrenPosts(childrenPosts), [childrenPosts]);
    // List of the children posts removed - these will be deleted on save.
    const [localRemovedChildren, setLocalRemovedChildren] = useState([]);
    const handleRemoveChild = (childPostId) => {
        const updatedChildren = localChildrenPosts.filter((child) => child.postId !== childPostId);
        setLocalChildrenPosts(updatedChildren);
        setLocalRemovedChildren((prevRemovedChildren) => [...prevRemovedChildren, childPostId]);
    };
    // Update parent post text and delete removed children posts.
    // TODO: Extract this logic as a hook for Create Post too
    const handleSave = () => {
        let mentionees = [];
        const postMetadata = {};
        if ((mentions === null || mentions === void 0 ? void 0 : mentions.length) > 0) {
            mentionees = [{}];
            mentionees[0].type = 'user';
            mentionees[0].userIds = mentions.map(({ id }) => id);
            postMetadata.mentioned = mentions.map(({ plainTextIndex, id, display }) => ({
                index: plainTextIndex,
                length: display.length,
                type: 'user',
                userId: id,
            }));
            postMetadata.markupText = markup;
        }
        localRemovedChildren.forEach((childPostId) => {
            PostRepository.deletePost(childPostId);
        });
        handleUpdatePost({ text }, extractMetadata(mentions));
        clearAll();
        onSave();
    };
    const isEmpty = useMemo(() => (text === null || text === void 0 ? void 0 : text.trim()) === '' && !localChildrenPosts.length, [text, localChildrenPosts]);
    const childFilePosts = useMemo(() => localChildrenPosts.filter((childPost) => childPost.dataType === PostDataType.FilePost), [localChildrenPosts]);
    const childImagePosts = useMemo(() => localChildrenPosts.filter((childPost) => childPost.dataType === PostDataType.ImagePost), [localChildrenPosts]);
    const childVideoPosts = useMemo(() => localChildrenPosts.filter((childPost) => childPost.dataType === PostDataType.VideoPost), [localChildrenPosts]);
    return (React.createElement(PostEditorContainer, { className: className },
        React.createElement(ContentContainer, null,
            React.createElement(Content, { id: postId, data: { text: markup, initialMentionees: postMentionees }, "data-qa-anchor": "post-editor-textarea", dataType: dataType, placeholder: formatMessage({ id: 'whatsGoingOnPlaceholder' }), queryMentionees: queryMentionees, onChange: onChange }),
            childImagePosts.length > 0 && (React.createElement(Content, { id: postId, data: childImagePosts, dataType: PostDataType.ImagePost, onRemoveChild: handleRemoveChild })),
            childVideoPosts.length > 0 && (React.createElement(Content, { id: postId, data: childVideoPosts, dataType: PostDataType.VideoPost, onRemoveChild: handleRemoveChild })),
            childFilePosts.length > 0 && (React.createElement(Content, { data: childFilePosts, dataType: PostDataType.FilePost, onRemoveChild: handleRemoveChild }))),
        React.createElement(Footer, null,
            React.createElement(PostButton, { "data-qa-anchor": "post-editor-save-button", disabled: isEmpty, onClick: handleSave },
                React.createElement(FormattedMessage, { id: "save" })))));
};
PostEditor.propTypes = {
    postId: PropTypes.string.isRequired,
    className: PropTypes.string,
    onSave: PropTypes.func,
};
PostEditor.defaultProps = {
    className: null,
    onSave: () => { },
};
export default memo(PostEditor);
//# sourceMappingURL=index.js.map